import Stepper from "@/components/common/Stepper";
import { AdminCreateUserWizardNav } from "@/features/admin/components/AdminCreateUserWizardNav";
import {
    AdminUserBillingCard,
    type AdminUserBillingCardHandle,
} from "@/features/admin/components/AdminUserBillingCard";
import { useAdminUserOrgRolesQuery } from "@/features/admin/api/selectUserOrgRolesByUserId";
import { getCreateUserWizardSteps, validateCreateUserWizardSearch } from "@/features/admin/utils/createUserWizard";
import { useProfileByIdQuerySafe } from "@/features/profile/api/selectProfile";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { ui } from "@patient-watch/ui";
import { useRef, useState } from "react";

export const Route = createFileRoute("/_app/users/create-user/billing")({
    validateSearch: validateCreateUserWizardSearch,
    beforeLoad: ({ search }) => {
        if (!search.userId) {
            throw redirect({ to: "/users/create-user/details" });
        }
    },
    component: CreateUserBillingStep,
});

function CreateUserBillingStep() {
    const search = Route.useSearch();
    const userId = search.userId!;
    const navigate = useNavigate();
    const billingRef = useRef<AdminUserBillingCardHandle>(null);
    const [isSaving, setIsSaving] = useState(false);
    const { data: profile } = useProfileByIdQuerySafe(userId);
    const { data: orgRoles = [], isLoading } = useAdminUserOrgRolesQuery(userId);

    const displayName =
        [profile?.first_name, profile?.last_name].filter(Boolean).join(" ") || profile?.email || "this user";

    const isBillable = orgRoles.some((row) => row.role === "CLINICIAN" || row.role === "SECRETARY");

    const handleContinue = async () => {
        if (isBillable) {
            setIsSaving(true);
            try {
                const saved = await billingRef.current?.save();
                if (!saved) return;
            } finally {
                setIsSaving(false);
            }
        }
        void navigate({
            to: "/users/create-user/impersonate",
            search: { userId },
        });
    };

    return (
        <div className="space-y-6" data-testid="create-user-billing-step">
            <Stepper steps={getCreateUserWizardSteps("billing", search)} />
            <div className="space-y-2">
                <h2 className="font-medium text-lg">Billing</h2>
                <p className="text-muted-foreground text-sm">
                    Set a subscription plan when this user is a clinician or secretary. Continue saves and moves on.
                </p>
            </div>

            {isLoading ? (
                <ui.Skeleton className="w-full h-40" />
            ) : isBillable ? (
                <AdminUserBillingCard ref={billingRef} userId={userId} userName={displayName} embedded hideSaveButton />
            ) : (
                <ui.Alert>
                    <ui.AlertTitle>Billing not required</ui.AlertTitle>
                    <ui.AlertDescription>
                        This user has no clinician or secretary role yet. You can continue, or go back to assign one.
                    </ui.AlertDescription>
                </ui.Alert>
            )}

            <AdminCreateUserWizardNav
                search={search}
                backTo="/users/create-user/templates"
                nextLabel={isBillable ? "Continue" : "Skip billing"}
                nextLoading={isSaving}
                onNextClick={() => void handleContinue()}
            />
        </div>
    );
}
